<template>
  <b-container fluid class="layout-wide py-5">
    <h2 class="font-weight-bold mb-4">
      <span class="text-primary">경영 관리</span> / 수입 지출 내역서
    </h2>

    <b-card class="border-0 shadow-sm rounded-lg mb-4">
      <b-row align-v="center">
        <b-col xl="5" lg="12" class="mb-3 mb-xl-0">
          <div class="d-flex align-items-center flex-wrap">
            <b-form-group label="기간" label-class="small font-weight-bold text-muted" class="mb-0 mr-3">
              <div class="d-flex">
                <b-form-select v-model="currentYear" :options="yearOptions" class="jinro-input mr-1" style="width: 100px" @change="onYearChange"></b-form-select>
                <b-form-select v-model="currentMonth" :options="monthOptions" class="jinro-input" style="width: 80px" @change="fetchList"></b-form-select>
              </div>
            </b-form-group>
            <b-form-group label="구분" label-class="small font-weight-bold text-muted" class="mb-0 mr-2">
              <b-form-select v-model="search.type" :options="['전체', '수입', '지출', '기타']" class="jinro-input" style="width: 100px" @change="onTypeChange"></b-form-select>
            </b-form-group>
            <b-form-group label="분류" label-class="small font-weight-bold text-muted" class="mb-0 mr-3">
              <b-form-select v-model="search.category" :options="searchCategoryOptions" class="jinro-input" style="width: 140px" @change="fetchList"></b-form-select>
            </b-form-group>
            <b-button variant="primary" @click="fetchList" class="rounded-pill px-3 font-weight-bold shadow-sm mt-3 mt-sm-0" style="height: 38px;">조회</b-button>
          </div>
        </b-col>

        <b-col xl="7" lg="12">
          <div class="d-flex align-items-center justify-content-xl-end justify-content-start flex-wrap bg-light p-3 rounded">
            <div class="mr-4 d-flex align-items-center">
              <span class="small text-muted mr-2">총 수입</span>
              <strong class="text-primary h5 mb-0">{{ totalIncome.toLocaleString() }}</strong>
            </div>
            <div class="mr-4 d-flex align-items-center">
              <span class="small text-muted mr-2">총 지출</span>
              <strong class="text-danger h5 mb-0">{{ totalExpense.toLocaleString() }}</strong>
            </div>
            <div class="mr-4 d-flex align-items-center border-left pl-4">
              <span class="small text-muted mr-2 font-weight-bold">총 합계</span>
              <strong :class="totalBalance >= 0 ? 'text-success' : 'text-danger'" class="h4 mb-0">
                {{ totalBalance.toLocaleString() }}
              </strong>
              <span class="small text-muted ml-1">원</span>
            </div>

            <b-button variant="success" @click="downloadExcel" class="rounded-pill px-3 shadow-sm ml-auto mr-2">Excel</b-button>
            <b-button variant="dark" @click="executePrint" class="rounded-pill px-4 shadow-sm mr-2">인쇄</b-button>
            <b-button variant="primary" @click="openModal(null)" class="rounded-pill px-4 shadow-sm">내역 등록</b-button>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-card class="border-0 shadow-sm rounded-lg p-0 overflow-hidden mb-4">
      <b-table
          hover
          :items="items"
          :fields="fields"
          head-variant="light"
          class="mb-0 custom-table"
          @row-clicked="openModal"
      >
        <template #cell(transDate)="data">{{ data.value }}</template>
        <template #cell(type)="data">
          <b-badge :variant="getTypeVariant(data.value)" class="px-2 py-1">{{ data.value }}</b-badge>
        </template>
        <template #cell(incomeAmount)="data">
          <span v-if="data.value > 0" class="text-primary font-weight-bold">+{{ data.value.toLocaleString() }}</span>
          <span v-else class="text-muted opacity-50">-</span>
        </template>
        <template #cell(expenseAmount)="data">
          <span v-if="data.value > 0" class="text-danger font-weight-bold">-{{ data.value.toLocaleString() }}</span>
          <span v-else class="text-muted opacity-50">-</span>
        </template>
      </b-table>
      <div v-if="items.length === 0" class="text-center py-5 text-muted">데이터가 없습니다.</div>
    </b-card>

    <b-row v-if="items.length > 0">
      <b-col lg="12" class="mb-4">
        <b-card title="일자별 수입/지출 흐름" class="border-0 shadow-sm rounded-lg h-100">
          <div style="height: 300px; position: relative;">
            <canvas id="dailyChart"></canvas>
          </div>
        </b-card>
      </b-col>

      <b-col lg="6" class="mb-4">
        <b-card title="수입 분류별 점유율" class="border-0 shadow-sm rounded-lg h-100">
          <div v-if="totalIncome > 0" style="height: 300px; position: relative;">
            <canvas id="incomePieChart"></canvas>
          </div>
          <div v-else class="text-center text-muted py-5">수입 내역이 없습니다.</div>
        </b-card>
      </b-col>

      <b-col lg="6" class="mb-4">
        <b-card title="지출 분류별 점유율" class="border-0 shadow-sm rounded-lg h-100">
          <div v-if="totalExpense > 0" style="height: 300px; position: relative;">
            <canvas id="expensePieChart"></canvas>
          </div>
          <div v-else class="text-center text-muted py-5">지출 내역이 없습니다.</div>
        </b-card>
      </b-col>
    </b-row>

    <b-modal id="modal-account" :title="form.id ? '내역 수정' : '내역 등록'" hide-footer size="lg">
      <b-form @submit.prevent="saveBook">
        <b-row>
          <b-col md="6"><b-form-group label="날짜"><b-form-input type="date" v-model="form.transDate" required></b-form-input></b-form-group></b-col>
          <b-col md="6"><b-form-group label="구분"><b-form-radio-group v-model="form.type" :options="['수입', '지출', '기타']" buttons button-variant="outline-primary" @change="onFormTypeChange"></b-form-radio-group></b-form-group></b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group>
              <template #label>분류 (종류)<b-badge href="#" variant="secondary" @click="showCategoryModal = true" class="ml-2 pointer">관리</b-badge></template>
              <b-form-select v-model="form.categoryName" :options="formCategoryOptions" required><template #first><option :value="null">선택하세요</option></template></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6"><b-form-group label="수입 금액"><b-form-input v-model="formattedIncome" :disabled="form.type === '지출' || form.expenseAmount > 0" placeholder="0" class="text-right"></b-form-input></b-form-group></b-col>
          <b-col md="6"><b-form-group label="지출 금액"><b-form-input v-model="formattedExpense" :disabled="form.type === '수입' || form.incomeAmount > 0" placeholder="0" class="text-right"></b-form-input></b-form-group></b-col>
        </b-row>
        <b-form-group label="상세내용"><b-form-input v-model="form.description" placeholder="내용을 입력하세요"></b-form-input></b-form-group>
        <b-form-group label="비고"><b-form-textarea v-model="form.memo" rows="3"></b-form-textarea></b-form-group>
        <div class="d-flex justify-content-end">
          <b-button v-if="form.id" variant="danger" @click="deleteBook" class="mr-auto">삭제</b-button>
          <b-button variant="secondary" @click="$bvModal.hide('modal-account')" class="mr-2">취소</b-button>
          <b-button type="submit" variant="primary">저장</b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal v-model="showCategoryModal" title="분류(종류) 관리" hide-footer>
      <div class="mb-3">
        <b-input-group>
          <b-form-select v-model="newCatType" :options="['수입', '지출', '기타']" style="max-width: 30%"></b-form-select>
          <b-form-input v-model="newCatName" placeholder="새 분류명 입력"></b-form-input>
          <b-input-group-append><b-button variant="primary" @click="addCategory">추가</b-button></b-input-group-append>
        </b-input-group>
      </div>
      <b-list-group>
        <b-list-group-item v-for="cat in allCategories" :key="cat.id" class="d-flex justify-content-between align-items-center">
          <span><b-badge :variant="getTypeVariant(cat.type)" class="mr-2">{{ cat.type }}</b-badge>{{ cat.name }}</span>
          <b-button size="sm" variant="outline-danger" @click="deleteCategory(cat.id)">삭제</b-button>
        </b-list-group-item>
      </b-list-group>
    </b-modal>

    <div id="print-area" class="d-none">
      <div id="account-print-content">
        <div class="header-section">
          <h1 class="main-title">수입 지출 내역서</h1>
          <div class="sub-title">기간: {{ currentYear }}년 {{ currentMonth }}월</div>
        </div>

        <table class="summary-table">
          <tr>
            <th class="summary-th">총 수입</th>
            <td class="summary-td text-primary">{{ totalIncome.toLocaleString() }} 원</td>
            <th class="summary-th">총 지출</th>
            <td class="summary-td text-danger">{{ totalExpense.toLocaleString() }} 원</td>
            <th class="summary-th" style="background-color: #e2e6ea;">순수익</th>
            <td class="summary-td" style="background-color: #e2e6ea;">
              {{ totalBalance.toLocaleString() }} 원
            </td>
          </tr>
        </table>

        <table class="list-table">
          <thead>
          <tr>
            <th class="col-date">날짜</th>
            <th class="col-type">구분</th>
            <th class="col-category">분류</th>
            <th class="col-money">수입</th>
            <th class="col-money">지출</th>
            <th class="col-desc">내용</th>
            <th class="col-note">비고</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, idx) in items" :key="idx">
            <td class="col-date">{{ item.transDate }}</td>
            <td class="col-type">
                <span :class="{'type-income': item.type === '수입', 'type-expense': item.type === '지출'}">
                  {{ item.type }}
                </span>
            </td>
            <td class="col-category">{{ item.categoryName }}</td>
            <td class="col-money">
              {{ item.incomeAmount > 0 ? item.incomeAmount.toLocaleString() : '-' }}
            </td>
            <td class="col-money">
              {{ item.expenseAmount > 0 ? item.expenseAmount.toLocaleString() : '-' }}
            </td>
            <td class="col-desc">{{ item.description }}</td>
            <td class="col-note">{{ item.memo }}</td>
          </tr>
          <tr v-if="items.length === 0">
            <td colspan="7" style="text-align: center; height: 100px; color: #888;">
              해당 기간에 조회된 내역이 없습니다.
            </td>
          </tr>
          </tbody>
        </table>

        <div class="footer-date">
          출력일: {{ new Date().getFullYear() }}.{{ new Date().getMonth() + 1 }}.{{ new Date().getDate() }}
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>
// [신규] Chart.js 등록
import { Chart, registerables } from 'chart.js';
import { printAccountBook } from '@/utils/accountBookPrint'; // [신규] import 추가
Chart.register(...registerables);

export default {
  data() {
    const today = new Date();
    return {
      currentYear: today.getFullYear(),
      currentMonth: today.getMonth() + 1,
      search: { type: '전체', category: '전체' },
      items: [],
      allCategories: [],
      availableDates: [],
      form: { id: null, transDate: today.toISOString().split('T')[0], type: '지출', categoryName: null, incomeAmount: 0, expenseAmount: 0, description: '', memo: '', updatedAt: null },
      showCategoryModal: false, newCatType: '지출', newCatName: '',
      fields: [
        { key: 'transDate', label: '날짜', sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { width: '110px' } },
        { key: 'type', label: '구분', thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { width: '80px' } },
        { key: 'categoryName', label: '분류', thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { width: '120px' } },
        { key: 'incomeAmount', label: '수입', thClass: 'text-center', tdClass: 'text-right align-middle', thStyle: { width: '130px' } },
        { key: 'expenseAmount', label: '지출', thClass: 'text-center', tdClass: 'text-right align-middle', thStyle: { width: '130px' } },
        { key: 'description', label: '내용', tdClass: 'align-middle' },
        { key: 'memo', label: '비고', thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { width: '150px' } }
      ],
      // [신규] 차트 인스턴스 저장용 변수
      dailyChart: null,
      incomePieChart: null,
      expensePieChart: null,
      // 차트 색상 팔레트
      chartColors: ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6610f2', '#fd7e14', '#20c997', '#e83e8c', '#6c757d']
    };
  },
  computed: {
    yearOptions() {
      const thisYear = new Date().getFullYear();
      const years = new Set([thisYear]);
      this.availableDates.forEach(d => years.add(d.y));
      return Array.from(years).sort((a, b) => b - a);
    },
    monthOptions() {
      const thisYear = new Date().getFullYear();
      if (this.currentYear === thisYear) { return Array.from({length: 12}, (_, i) => i + 1); }
      else {
        const months = this.availableDates.filter(d => d.y === this.currentYear).map(d => d.m);
        return months.length > 0 ? months.sort((a, b) => a - b) : [1];
      }
    },
    searchCategoryOptions() {
      let cats = this.allCategories;
      if (this.search.type !== '전체') { cats = cats.filter(c => c.type === this.search.type); }
      const uniqueNames = [...new Set(cats.map(c => c.name))];
      return ['전체', ...uniqueNames];
    },
    formCategoryOptions() { return this.allCategories.filter(c => c.type === this.form.type).map(c => c.name); },
    totalIncome() { return this.items.reduce((acc, cur) => acc + (cur.incomeAmount || 0), 0); },
    totalExpense() { return this.items.reduce((acc, cur) => acc + (cur.expenseAmount || 0), 0); },
    totalBalance() { return this.totalIncome - this.totalExpense; },
    formattedIncome: { get() { return this.form.incomeAmount ? this.form.incomeAmount.toLocaleString() : ''; }, set(val) { this.form.incomeAmount = Number(val.replace(/[^0-9]/g, '')); } },
    formattedExpense: { get() { return this.form.expenseAmount ? this.form.expenseAmount.toLocaleString() : ''; }, set(val) { this.form.expenseAmount = Number(val.replace(/[^0-9]/g, '')); } }
  },
  async mounted() {
    await this.fetchCategories();
    await this.fetchAvailableDates();
    this.fetchList();
  },
  methods: {
    getCategoryColor(categoryName) {
      // 색상 팔레트 (두 파일에 똑같이 복사해서 넣어야 합니다)
      const colors = [
        '#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8',
        '#6610f2', '#fd7e14', '#20c997', '#e83e8c', '#6c757d',
        '#343a40', '#6f42c1', '#e0a800', '#c82333', '#138496'
      ];

      if (!categoryName) return '#ccc'; // 이름 없을 때 회색

      // 문자열을 숫자로 변환 (Hash)
      let hash = 0;
      for (let i = 0; i < categoryName.length; i++) {
        hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
      }

      // 색상 팔레트 개수만큼 나눠서 인덱스 결정 (항상 같은 이름 = 같은 인덱스)
      const index = Math.abs(hash % colors.length);
      return colors[index];
    },
    async fetchList() {
      try {
        const res = await this.$axios.get('/api/account/list', {
          params: { userId: localStorage.getItem('userId'), year: this.currentYear, month: this.currentMonth, type: this.search.type, category: this.search.category }
        });
        this.items = res.data.data;
        // [신규] 리스트 로드 후 차트 렌더링
        this.$nextTick(() => { this.renderCharts(); });
      } catch (e) { console.error(e); }
    },
    // [신규] 구분(수입/지출/기타) 변경 시 호출되는 메서드
    onFormTypeChange(type) {
      // 1. 카테고리 초기화 (기존 로직 유지)
      this.form.categoryName = null;

      // 2. 타입에 따라 반대쪽 금액 초기화
      if (type === '수입') {
        this.form.expenseAmount = 0; // 지출 금액 0으로 리셋
      } else if (type === '지출') {
        this.form.incomeAmount = 0;  // 수입 금액 0으로 리셋
      }
      // '기타'인 경우 둘 다 입력 가능하므로 값 유지
    },
    // [신규] 차트 렌더링 메인 함수
    renderCharts() {
      if (this.items.length === 0) return;

      const { dailyLabels, dailyIncome, dailyExpense, incomeCats, expenseCats } = this.processChartData();

      // 1. 일자별 흐름 (막대)
      this.drawDailyChart(dailyLabels, dailyIncome, dailyExpense);

      // 2. 수입 분류 (파이)
      if (Object.keys(incomeCats).length > 0) {
        this.drawPieChart('incomePieChart', incomeCats, '수입', this.incomePieChart, (chart) => this.incomePieChart = chart);
      } else if (this.incomePieChart) {
        this.incomePieChart.destroy();
        this.incomePieChart = null;
      }

      // 3. 지출 분류 (파이)
      if (Object.keys(expenseCats).length > 0) {
        this.drawPieChart('expensePieChart', expenseCats, '지출', this.expensePieChart, (chart) => this.expensePieChart = chart);
      } else if (this.expensePieChart) {
        this.expensePieChart.destroy();
        this.expensePieChart = null;
      }
    },

    // 데이터 가공 로직
    processChartData() {
      // 일자별 (1일~마지막일)
      const lastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();
      const dailyMap = {};
      for (let i = 1; i <= lastDay; i++) { dailyMap[i] = { income: 0, expense: 0 }; }

      // 카테고리별 집계
      const incomeCats = {};
      const expenseCats = {};

      this.items.forEach(item => {
        const day = parseInt(item.transDate.split('-')[2]);
        // 일자별 합계
        if (dailyMap[day]) {
          dailyMap[day].income += item.incomeAmount || 0;
          dailyMap[day].expense += item.expenseAmount || 0;
        }
        // 카테고리별 합계
        if (item.incomeAmount > 0) {
          const cat = item.categoryName || '미분류';
          incomeCats[cat] = (incomeCats[cat] || 0) + item.incomeAmount;
        }
        if (item.expenseAmount > 0) {
          const cat = item.categoryName || '미분류';
          expenseCats[cat] = (expenseCats[cat] || 0) + item.expenseAmount;
        }
      });

      return {
        dailyLabels: Object.keys(dailyMap).map(d => d + '일'),
        dailyIncome: Object.values(dailyMap).map(v => v.income),
        dailyExpense: Object.values(dailyMap).map(v => v.expense),
        incomeCats,
        expenseCats
      };
    },

    // 일자별 차트 그리기
    drawDailyChart(labels, incomeData, expenseData) {
      const ctx = document.getElementById('dailyChart');
      if (!ctx) return;

      if (this.dailyChart) this.dailyChart.destroy();

      this.dailyChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            { label: '수입', data: incomeData, backgroundColor: '#007bff', borderRadius: 4 }, // Primary Blue
            { label: '지출', data: expenseData, backgroundColor: '#dc3545', borderRadius: 4 } // Danger Red
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', align: 'end' },
            tooltip: { callbacks: { label: (c) => c.dataset.label + ': ' + c.raw.toLocaleString() + '원' } }
          },
          scales: {
            y: { beginAtZero: true, ticks: { callback: (v) => v.toLocaleString() } },
            x: { grid: { display: false } }
          }
        }
      });
    },

    // 파이 차트 그리기 (공통)
    drawPieChart(elementId, dataMap, labelPrefix, chartInstance, setChartInstance) {
      const ctx = document.getElementById(elementId);
      if (!ctx) return;
      if (chartInstance) chartInstance.destroy();

      const labels = Object.keys(dataMap);
      const data = Object.values(dataMap);

      const newChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: labels.map(name => this.getCategoryColor(name)),
            hoverOffset: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { boxWidth: 12 } },
            tooltip: {
              callbacks: {
                label: function(c) {
                  const value = c.raw;
                  const total = c.chart._metasets[c.datasetIndex].total;
                  const percentage = ((value / total) * 100).toFixed(1) + '%';
                  return c.label + ': ' + value.toLocaleString() + '원 (' + percentage + ')';
                }
              }
            }
          }
        }
      });
      setChartInstance(newChart);
    },

    // 기존 메서드 유지
    async fetchAvailableDates() {
      try {
        const res = await this.$axios.get('/api/account/available-dates', { params: { userId: localStorage.getItem('userId') } });
        this.availableDates = res.data.data;
      } catch (e) { console.error(e); }
    },
    onYearChange() { this.$nextTick(() => { if (!this.monthOptions.includes(this.currentMonth)) { this.currentMonth = this.monthOptions[0]; } this.fetchList(); }); },
    onTypeChange() { this.search.category = '전체'; this.fetchList(); },
    getTypeVariant(type) { if (type === '수입') return 'primary'; if (type === '지출') return 'danger'; return 'secondary'; },
    async fetchCategories() {
      try { const res = await this.$axios.get('/api/account/categories', { params: { userId: localStorage.getItem('userId') } }); this.allCategories = res.data.data; } catch (e) { console.error(e); }
    },
    openModal(item) {
      if (item) { this.form = { ...item }; }
      else {
        let defaultDate = new Date(); defaultDate.setFullYear(this.currentYear); defaultDate.setMonth(this.currentMonth - 1);
        this.form = { id: null, transDate: defaultDate.toISOString().split('T')[0], type: '지출', categoryName: null, incomeAmount: 0, expenseAmount: 0, description: '', memo: '', updatedAt: null };
      }
      this.$bvModal.show('modal-account');
    },
    async saveBook() { try { await this.$axios.post('/api/account/save', this.form, { params: { userId: localStorage.getItem('userId') } }); this.$bvModal.hide('modal-account'); this.fetchList(); this.fetchAvailableDates(); } catch (e) { alert('저장 실패'); } },
    async deleteBook() { if(!confirm("정말 삭제하시겠습니까?")) return; try { await this.$axios.delete(`/api/account/${this.form.id}`); this.$bvModal.hide('modal-account'); this.fetchList(); this.fetchAvailableDates(); } catch (e) { alert('삭제 실패'); } },
    async addCategory() { if(!this.newCatName) return; try { await this.$axios.post('/api/account/category', { userId: localStorage.getItem('userId'), type: this.newCatType, name: this.newCatName }); this.newCatName = ''; this.fetchCategories(); } catch (e) { alert('추가 실패'); } },
    async deleteCategory(id) { try { await this.$axios.delete(`/api/account/category/${id}`); this.fetchCategories(); } catch (e) { alert('삭제 실패'); } },
    // [신규] 엑셀 다운로드
    async downloadExcel() {
      try {
        const response = await this.$axios.get('/api/account/excel', {
          params: {
            userId: localStorage.getItem('userId'),
            year: this.currentYear,
            month: this.currentMonth,
            type: this.search.type,
            category: this.search.category
          },
          responseType: 'blob' // [중요] 파일 다운로드를 위해 반드시 설정
        });

        // 1. 헤더에서 파일명 추출
        const contentDisposition = response.headers['content-disposition'];
        let fileName = `수입지출내역서_${this.currentYear}년${this.currentMonth}월.xlsx`; // 기본값 (추출 실패 시 사용)

        if (contentDisposition) {
          // "filename=" 뒤에 있는 값을 정규식으로 찾음
          const fileNameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
          if (fileNameMatch && fileNameMatch.length === 2) {
            // URL 디코딩 (백엔드에서 URLEncoder로 인코딩했으므로 필수)
            // .replace(/\+/g, ' ') : 공백이 + 기호로 변환된 것을 다시 공백으로 변경
            fileName = decodeURIComponent(fileNameMatch[1].replace(/\+/g, ' '));
          }
        }

        // 브라우저에서 다운로드 트리거
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // 파일명 설정
        // const fileName = `수입지출내역서_${this.currentYear}년${this.currentMonth}월.xlsx`;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();

        // 뒷정리
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (e) {
        console.error("엑셀 다운로드 실패", e);
        alert("엑셀 다운로드 중 오류가 발생했습니다.");
      }
    },
    // [신규] 인쇄 실행 함수 추가
    executePrint() {
      printAccountBook('account-print-content', {
        year: this.currentYear,
        month: this.currentMonth
      });
    }
  }
};
</script>

<style scoped>
/* 기존 스타일 유지 + 인쇄 영역 숨김 처리 */
#print-area {
  display: none !important;
}
.jinro-input { border-radius: 12px; height: 38px; border: 1px solid #ced4da; }
::v-deep .custom-table thead th { background-color: #f8f9fa !important; color: #495057; font-weight: bold; border-bottom: 2px solid #dee2e6; }
.pointer { cursor: pointer; }
</style>
